import React, { useEffect, useState } from "react";
import Select from "react-select";
import _ from "lodash";
import makeAnimated from "react-select/animated";
import { MdAddCircleOutline, MdDeleteForever } from "react-icons/md";

// This is the Multi-Workout Builder's per-column analog of `ProgramSectionAndMatrix` in
// `ProgramMovementMatrix.jsx` — same immutable-callback-driven category/movement editing, minus
// the -3..3 difficulty matrix cells (this screen is builder-only, no matrix).
//
// Presentation matches the approved mockup: a read-first, click-to-edit card per builder
// category ("section") — a solid blue header block (title/sets/reps) sitting above a list of
// movement rows on a light body, rather than always-visible bordered <Input>/<Select> fields.
// None of the underlying immutable edit handlers changed — only how/when they're invoked.

const WORKOUT_SELECT_STYLE_SETTINGS = {
  control: (provided) => ({
    ...provided,
    minHeight: "32px",
    fontSize: "0.8rem",
    boxShadow: "none",
  }),
  valueContainer: (provided) => ({
    ...provided,
    // Keep single-line values (see the identical comment in ProgramMovementMatrix.jsx) so every
    // movement row stays the same height regardless of option label length.
    flexWrap: "nowrap",
    padding: "0 6px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    padding: "0 4px",
  }),
  option: (provided) => ({
    ...provided,
    fontSize: "0.8rem",
    padding: "4px 8px",
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: "0.8rem",
  }),
  // Portaled to <body> so the menu isn't clipped by the section card's `overflow: hidden`.
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const MENU_PORTAL_TARGET = typeof document !== "undefined" ? document.body : null;

// Built once — see the identical comment in ProgramMovementMatrix.jsx for why this must not be
// created inline in render.
const animatedComponents = makeAnimated();

// Small inline click-to-edit text control used for the section title/sets/reps lines. Read mode
// renders plain text plus a pencil icon; clicking either (the text or the pencil — both are part
// of the same button, satisfying "a pencil icon, or click-the-text") enters edit mode. Commits on
// blur/Enter, discards on Escape. `onCommit` is only invoked when the value actually changed, and
// is always the exact same immutable handler this field already called — only WHEN it's called
// changed (once, on commit, instead of on every keystroke).
function EditableText({ value, placeholder, onCommit, textClassName, ariaLabel }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    if (!editing) setDraft(value || "");
  }, [value, editing]);

  function commit() {
    setEditing(false);
    if ((value || "") !== (draft || "")) {
      onCommit(draft);
    }
  }

  function cancel() {
    setDraft(value || "");
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        autoFocus
        type="text"
        className="workout-builder-inline-input"
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancel();
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className={`workout-builder-editable ${textClassName || ""}`}
      onClick={() => setEditing(true)}
      aria-label={ariaLabel}
    >
      <span className="workout-builder-editable-text">{value || placeholder}</span>
    </button>
  );
}

// Memoized so that editing one movement (or a duplicate-count recompute elsewhere on the page)
// doesn't re-render every other row's react-select. `WorkoutSection`'s own render function
// re-runs whenever the page recomputes its shared `duplicatedMovements` map (a new Map
// reference on essentially every keystroke in any column), but each `MovementRow`'s own props
// (`isDuplicate`, `duplicateCount`) are plain primitives that only change when THIS row's own
// duplicate status changes — React.memo bails out per-row on unchanged primitive props
// regardless of the parent having re-executed.
const MovementRow = React.memo(function MovementRow({
  categorySeq,
  movement,
  mvmtList,
  isDuplicate,
  duplicateCount,
  highlightDuplicates,
  onChangeMovement,
  onAddMovement,
  onRemoveMovement,
}) {
  const [editing, setEditing] = useState(false);
  const showHighlight = highlightDuplicates && isDuplicate;
  const selectedOption = _.find(mvmtList, (mvmt) => mvmt.value === movement.movementId);

  return (
    <div className={`workout-builder-movement-row ${showHighlight ? "workout-builder-duplicate" : ""}`}>
      {editing ? (
        <div className="flex-1 min-w-0">
          <Select
            autoFocus
            openMenuOnFocus
            closeMenuOnSelect
            components={animatedComponents}
            options={mvmtList ? mvmtList : []}
            value={selectedOption}
            placeholder="Choose movement…"
            menuPortalTarget={MENU_PORTAL_TARGET}
            menuPosition="fixed"
            onChange={(mObj) => {
              if (mObj) onChangeMovement(categorySeq, movement.seq, mObj);
              setEditing(false);
            }}
            onBlur={() => setEditing(false)}
            styles={WORKOUT_SELECT_STYLE_SETTINGS}
          />
        </div>
      ) : (
        // Click-to-edit: the whole movement name is the trigger — clicking it swaps this row for
        // the <Select> above (which autofocuses/opens its menu immediately). Rendered as a button
        // for keyboard/AT parity with the former pencil control.
        <button
          type="button"
          className="workout-builder-movement-name"
          aria-label={`Edit movement: ${movement.name || "unset"}`}
          onClick={() => setEditing(true)}
        >
          {movement.name || "Select movement…"}
        </button>
      )}
      {showHighlight ? (
        <span
          className="workout-builder-duplicate-badge"
          title={`This movement's category also appears in ${duplicateCount} visible workouts.`}
        >
          {duplicateCount}x
        </span>
      ) : null}
      <button
        type="button"
        className="workout-builder-icon-btn add"
        title="Add movement below"
        aria-label="Add movement below"
        onClick={() => onAddMovement(categorySeq, movement.seq)}
      >
        <MdAddCircleOutline aria-hidden="true" />
      </button>
      <button
        type="button"
        className="workout-builder-icon-btn danger"
        title="Remove movement"
        aria-label="Remove movement"
        onClick={() => onRemoveMovement(categorySeq, movement.seq)}
      >
        <MdDeleteForever aria-hidden="true" />
      </button>
    </div>
  );
});

const WorkoutSection = React.memo(function WorkoutSection({
  category,
  mvmtList,
  duplicatedMovements,
  highlightDuplicates,
  changeCategoryMovementCallback,
  changeHeaderCallback,
  addSectionCallback,
  removeCategoryCallback,
  addMovementCallback,
  removeMovementCallback,
}) {
  const seq = category.category.seq;
  const movements = category.movements || [];

  return (
    <div className="workout-builder-section">
      <div className="workout-builder-section-header">
        <EditableText
          value={category.category.header}
          placeholder="Section name"
          onCommit={(v) => changeHeaderCallback(seq, "header", v)}
          textClassName="workout-builder-section-title"
          ariaLabel="Edit section name"
        />
        <EditableText
          value={category.category.setsSubheader}
          placeholder="Sets"
          onCommit={(v) => changeHeaderCallback(seq, "setsSubheader", v)}
          textClassName="workout-builder-section-subline"
          ariaLabel="Edit sets"
        />
        <EditableText
          value={category.category.repsSubheader}
          placeholder="Reps"
          onCommit={(v) => changeHeaderCallback(seq, "repsSubheader", v)}
          textClassName="workout-builder-section-subline"
          ariaLabel="Edit reps"
        />
        {/* .workout-builder-section-header-actions (see index.scss) pins the add(+)/delete controls
            to the section header's top-right corner, mirroring .workout-builder-remove-column's
            placement on the column header. Add inserts a new section directly below this one;
            delete removes this one. */}
        <div className="workout-builder-section-header-actions">
          <button
            type="button"
            className="workout-builder-icon-btn workout-builder-add-section-btn"
            title="Add section below"
            aria-label="Add section below"
            onClick={() => addSectionCallback(seq)}
          >
            <MdAddCircleOutline aria-hidden="true" />
          </button>
          <button
            type="button"
            className="workout-builder-icon-btn danger workout-builder-remove-section"
            title="Remove this section"
            aria-label="Remove this section"
            onClick={() => {
              if (window.confirm("Remove this section?")) removeCategoryCallback(seq);
            }}
          >
            <MdDeleteForever aria-hidden="true" />
          </button>
        </div>
      </div>
      <div className="workout-builder-section-body">
        {_.map(movements, (m) => {
          const duplicateCount = duplicatedMovements
            ? duplicatedMovements.get(m.movementId)
            : undefined;
          return (
            <MovementRow
              key={`${seq}-${m.seq}-${m.movementId}`}
              categorySeq={seq}
              movement={m}
              mvmtList={mvmtList}
              isDuplicate={!!duplicateCount}
              duplicateCount={duplicateCount || 0}
              highlightDuplicates={highlightDuplicates}
              onChangeMovement={changeCategoryMovementCallback}
              onAddMovement={addMovementCallback}
              onRemoveMovement={removeMovementCallback}
            />
          );
        })}
        {/* Per-row add(+) buttons are the primary way to add movements. When a section has been
            emptied (last row deleted), there's no row to hang a (+) on, so surface a single
            bootstrap link that inserts the first blank row (seq 0 -> inserted at the top). */}
        {movements.length === 0 ? (
          <button
            type="button"
            className="workout-builder-add-link"
            onClick={() => addMovementCallback(seq, 0)}
          >
            + Add movement
          </button>
        ) : null}
      </div>
    </div>
  );
});

export default WorkoutSection;
